// API Routes Configuration
export const API_ROUTES = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
  },

  // Users
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    CLIENT_PORTAL_PUBLIC: (shareToken: string) => `/users/client-portal/${shareToken}`,
    UPDATE_AVATAR: '/users/avatar',
    UPDATE_PASSWORD: '/users/password',
  },

  CLIENT_PORTALS: {
    BASE: '/client-portals',
    BY_SHARE_TOKEN: (shareToken: string) => `/client-portals/${shareToken}`,
    WORKFLOW_SESSION: (workflowId: string) => `/client-portals/workflows/${workflowId}`,
    QUOTE_RESPONSE: (shareToken: string) => `/client-portals/${shareToken}/quote-response`,
    SIGN_CONTRACT: (shareToken: string) => `/client-portals/${shareToken}/sign-contract`,
    QUESTIONNAIRE: (shareToken: string) => `/client-portals/${shareToken}/questionnaire`,
  },

  // Licenses
  LICENSES: {
    BASE: '/licenses',
    VALIDATE: '/licenses/validate',
    ACTIVATE: '/licenses/activate',
    DEACTIVATE: '/licenses/deactivate',
  },

  // Conversations
  CONVERSATIONS: {
    BASE: '/conversations',
    BY_ID: (id: string) => `/conversations/${id}`,
    MESSAGES: (id: string) => `/conversations/${id}/messages`,
  },

  // Contacts/Leads
  CONTACTS: {
    BASE: '/contacts',
    CREATE_MANUAL: '/contacts/manual',
    BY_ID: (id: string) => `/contacts/${id}`,
    EXPORT: '/contacts/export',
  },

  // Training Data
  TRAINING: {
    BASE: '/training',
    BY_ID: (id: string) => `/training/${id}`,
    UPLOAD: '/training/upload',
  },

  // Widget Settings
  WIDGET: {
    BASE: '/widget',
    SETTINGS: '/widget/settings',
    EMBED_CODE: '/widget/embed-code',
  },

  // Analytics
  ANALYTICS: {
    BASE: '/analytics',
    DASHBOARD: '/analytics/dashboard',
    CONVERSATIONS: '/analytics/conversations',
    LEADS: '/analytics/leads',
  },

  // Subscriptions & Billing
  BILLING: {
    BASE: '/billing',
    SUBSCRIPTIONS: '/billing/subscriptions',
    INVOICES: '/billing/invoices',
    PAYMENT_METHODS: '/billing/payment-methods',
    CREATE_CHECKOUT: '/billing/create-checkout-session',
    CREATE_PORTAL: '/billing/create-portal',
  },

  // Sync utilities
  SYNC: {
    SUBSCRIPTION: '/sync/sync-subscription',
  },

  // Integrations
  INTEGRATIONS: {
    BASE: '/integrations',
    REQUEST: '/integrations/request',
    REQUESTS: '/integrations/requests',
    REQUEST_BY_ID: (id: string) => `/integrations/requests/${id}`,
    GOOGLE_CALENDAR: '/integrations/google-calendar',
    STRIPE: '/integrations/stripe',
  },

  // Admin
  ADMIN: {
    USERS: '/admin/users',
    LICENSES: '/admin/licenses',
    ROADMAP: '/admin/roadmap',
    CHANGELOG: '/admin/changelog',
    CONTENT: '/admin/content',
    ANALYTICS: '/admin/analytics',
  },

  // OpenAI
  AI: {
    CHAT: '/ai/chat',
    COMPLETE: '/ai/complete',
  },

  // Email
  EMAIL: {
    SEND: '/email/send',
    SCHEDULE: '/email/schedule',
    SCHEDULED_LIST: '/email/scheduled',
    SCHEDULED_DELETE: (id: string) => `/email/scheduled/${id}`,
    VERIFY: '/email/verify',
    SAVE: '/email/save',
    SETTINGS: '/email/settings',
    FETCH: '/email/fetch',
    SYNC: '/email/sync',
  },

  // Workflows
  WORKFLOWS: {
    BASE: '/workflows',
    BY_ID: (id: string) => `/workflows/${id}`,
    TASKS: (id: string) => `/workflows/${id}/tasks`,
    TOGGLE_TASK: (jobId: string, taskId: string) => `/workflows/${jobId}/tasks/${taskId}/toggle`,
    COMPLETE: (id: string) => `/workflows/${id}/complete`,
  },
};

// App Routes (Frontend)
export const APP_ROUTES = {
  HOME: '/',

  // Authentication
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  EMAIL_VERIFIED_SUCCESS: '/email-verified',
  ONBOARDING: '/onboarding',
  PLAN_SELECTION: '/plan-selection',
  JOIN_TEAM: '/join/:inviteId',

  // Dashboard
  DASHBOARD: '/dashboard',
  CONVERSATIONS: '/dashboard/conversations',
  CONVERSATION_DETAIL: '/dashboard/conversations/:id', // PLANNED: not yet routed
  EMAIL: '/dashboard/email',
  CONTACTS: '/dashboard/contacts',
  BOOKINGS: '/dashboard/bookings',
  BOOKINGS_WITH_TAB: (tab: string) => `/dashboard/bookings?tab=${tab}`,
  WORKFLOW: '/dashboard/workflow',
  SETTINGS: '/dashboard/settings',
  ANALYTICS: '/dashboard/analytics', // hidden — kept for reference
  INTEGRATIONS: '/dashboard/integrations',
  INTEGRATIONS_WITH_MODAL: (modal: string) => `/dashboard/integrations?modal=${modal}`, // PLANNED: not yet used
  BILLING: '/dashboard/billing',
  TRAINING_DATA: '/dashboard/training',
  WIDGET_SETTINGS: '/dashboard/widget',
  WIDGET_CODE: '/dashboard/widget/code', // PLANNED: not yet routed
  ACCOUNT_SETTINGS: '/dashboard/account-settings',
  HELP: '/dashboard/help',
  CHANGELOG: '/dashboard/changelog',
  TEAM: '/dashboard/team',

  // Admin
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_LICENSES: '/admin/licenses',
  ADMIN_ROADMAP: '/admin/roadmap',
  ADMIN_CHANGELOG: '/admin/changelog',
  ADMIN_CONTENT: '/admin/content',

  // Marketing Pages
  PRICING: '/pricing',
  FEATURES: '/features',
  SOLUTIONS: '/solutions',
  SOLUTIONS_PHOTOGRAPHERS: '/solutions/wedding-photographers',
  SOLUTIONS_VIDEOGRAPHERS: '/solutions/wedding-videographers',
  SOLUTIONS_FLORISTS: '/solutions/wedding-florists',
  SOLUTIONS_VENUES: '/solutions/wedding-venues',
  SOLUTIONS_COORDINATORS: '/solutions/wedding-coordinators',
  SOLUTIONS_DJS: '/solutions/wedding-djs',
  SOLUTIONS_HAIR_MAKEUP: '/solutions/hair-makeup-artists',
  SOLUTIONS_OFFICIANTS: '/solutions/wedding-officiants',
  SOLUTIONS_CALLIGRAPHERS: '/solutions/wedding-calligraphers',
  SOLUTIONS_DECOR_RENTALS: '/solutions/decor-rentals',
  SOLUTIONS_PHOTO_BOOTH: '/solutions/photo-booth',
  SOLUTIONS_TRANSPORTATION: '/solutions/wedding-transportation',
  SOLUTIONS_BRIDAL_BOUTIQUES: '/solutions/bridal-boutiques',
  COMING_SOON: '/coming-soon', // PLANNED: not yet routed
  TESTIMONIALS: '/testimonials', // PLANNED: not yet routed
  ABOUT: '/about', // PLANNED: not yet routed
  CONTACT: '/contact',
  ROADMAP: '/roadmap',
  CHANGELOG_PUBLIC: '/changelog',
  DOCUMENTATION: '/documentation',
  SUPPORT: '/support',
  AFFILIATE_PROGRAM: '/affiliate-program',
  COMMUNITY: '/community',
  REFUND_POLICY: '/refund-policy',
  DISCLAIMER: '/disclaimer',
  CLIENT_PORTAL_PUBLIC: '/client-portal/:shareToken',
  CLIENT_PORTAL_PUBLIC_BY_TOKEN: (shareToken: string) => `/client-portal/${shareToken}`,

  // Legal
  TERMS: '/terms',
  PRIVACY: '/privacy',

  // Payment
  PAYMENT_SUCCESS: '/payment/success', // PLANNED: not yet routed
  PAYMENT_CANCEL: '/payment/cancel', // PLANNED: not yet routed
};
